
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Reveal } from '../Reveal';
import { Search, Book, ChevronRight, Hash, Terminal } from 'lucide-react';
import { Page } from '../../types';

interface GlossaryTerm {
  term: string;
  definition: string;
  category: string;
  linksTo?: string;
}

export const GlossaryPage: React.FC<{ onNavigate?: (page: Page) => void }> = ({ onNavigate }) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeLetter, setActiveLetter] = useState<string | null>(null);

  const terms: GlossaryTerm[] = t('shared.glossary.terms', { returnObjects: true }) as GlossaryTerm[];

  const alphabet = Array.from("ABCDEFGHIJKLMNOPQRSTUVWXYZ");
  const filteredTerms = useMemo(() => {
    return terms.filter(item => {
      const matchesSearch = item.term.toLowerCase().includes(searchTerm.toLowerCase()) || item.definition.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesLetter = !activeLetter || item.term.toUpperCase().startsWith(activeLetter);
      return matchesSearch && matchesLetter;
    }).sort((a, b) => a.term.localeCompare(b.term));
  }, [searchTerm, activeLetter, terms, t]);

  const handleArticleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (onNavigate) onNavigate('ai-articles');
  };

  return (
    <div className="bg-white min-h-screen pt-24 font-sans text-gray-900">
      {/* Light Hero - Unified Size */}
      <section className="relative pt-20 pb-20 px-6 overflow-hidden bg-white border-b border-gray-100">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-red-50 rounded-full blur-[100px] pointer-events-none translate-x-1/4 -translate-y-1/4"></div>

        <div className="max-w-7xl mx-auto relative z-10">
          <Reveal>
            <div className="border-b border-black pb-12 mb-12">
              <div className="inline-block px-4 py-2 bg-gray-100 text-gray-600 text-[10px] font-bold uppercase tracking-[0.2em] mb-8 border border-gray-200">
                {t('shared.glossary.hero.badge')}
              </div>
              <h1 className="text-6xl md:text-8xl font-bold tracking-tighter leading-[0.85] mb-12">
                {t('shared.glossary.hero.title_part1', { defaultValue: 'DAS' })}<br />{t('shared.glossary.hero.title_part2', { defaultValue: 'GLOSSAR' })}<span className="text-clever-teal">.</span>
              </h1>

              {/* Minimalist Search Bar */}
              <div className="max-w-2xl relative group">
                <input
                  type="text"
                  placeholder={t('shared.glossary.hero.search_placeholder')}
                  className="w-full bg-transparent border-b-2 border-gray-200 py-6 text-2xl font-bold focus:outline-none focus:border-black transition-all placeholder:text-gray-200 uppercase tracking-tighter"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <Search className="absolute right-0 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-black transition-colors" size={24} />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* A-Z Index Bar - Clean & Sticky */}
      <nav className="sticky top-[80px] z-50 bg-white/95 backdrop-blur-md border-b border-gray-200 py-4">
        <div className="max-w-7xl mx-auto px-6 overflow-x-auto no-scrollbar">
          <div className="flex items-center gap-6 min-w-max">
            <button
              onClick={() => setActiveLetter(null)}
              className={`text-[10px] font-bold px-4 py-1 tracking-widest transition-colors ${!activeLetter ? 'bg-black text-white' : 'text-gray-300 hover:text-black'}`}
            >
              {t('shared.glossary.nav.all')}
            </button>
            {alphabet.map(l => (
              <button
                key={l}
                onClick={() => setActiveLetter(l)}
                className={`text-xs font-bold font-mono px-2 py-1 transition-all ${activeLetter === l ? 'text-clever-teal scale-150' : 'text-gray-200 hover:text-black'}`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Glossary Content Grid - Light Theme */}
      <section className="px-6 py-24 bg-white min-h-screen">
        <div className="max-w-7xl mx-auto">
          {filteredTerms.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-l border-black">
              {filteredTerms.map((item, idx) => (
                <Reveal key={item.term} delay={idx * 0.02}>
                  <div className="p-12 border-b border-r border-black h-full flex flex-col group relative hover:bg-gray-50 transition-colors overflow-hidden">
                    <span className="absolute top-4 right-4 text-8xl font-bold font-mono opacity-[0.02] pointer-events-none group-hover:opacity-[0.05] transition-opacity">
                      {item.term.charAt(0)}
                    </span>

                    <div className="mb-10">
                      <span className="text-[9px] font-mono font-bold text-gray-400 tracking-[0.2em] border border-gray-200 px-2 py-1 uppercase">{item.category}</span>
                    </div>

                    <h3 className="text-2xl font-bold tracking-tighter mb-6 group-hover:text-clever-teal transition-colors uppercase">
                      {item.term}
                    </h3>

                    <p className="text-gray-500 font-light leading-relaxed mb-10 text-base">
                      {item.definition}
                    </p>

                    {item.linksTo && (
                      <div className="mt-auto pt-6 border-t border-gray-100">
                        <button
                          onClick={handleArticleClick}
                          className="flex items-center gap-3 text-[10px] font-bold uppercase tracking-widest text-black hover:text-clever-teal transition-colors"
                        >
                          {t('shared.glossary.item.view_docs')} <ChevronRight size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                </Reveal>
              ))}
            </div>
          ) : (
            <div className="text-center py-40 border-t border-gray-100">
              <div className="text-9xl font-bold tracking-tighter text-gray-50 mb-8 font-mono">000</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 uppercase tracking-widest">{t('shared.glossary.empty.title')}</h3>
              <button onClick={() => { setSearchTerm(''); setActiveLetter(null); }} className="text-clever-teal font-bold underline text-sm uppercase tracking-widest">{t('shared.glossary.empty.reset')}</button>
            </div>
          )}
        </div>
      </section>

      {/* Industrial Footer Section - Light Version */}
      <section className="py-32 px-6 border-t border-gray-200 bg-gray-50">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row gap-16 items-center">
          <div className="flex-1">
            <Reveal>
              <h2 className="text-4xl md:text-6xl font-bold tracking-tighter leading-[0.9] mb-8">
                {t('shared.glossary.footer.title_part1')} <br /> <span className="text-gray-400 uppercase">{t('shared.glossary.footer.title_part2')}</span>
              </h2>
              <p className="text-xl text-gray-500 font-light max-w-xl leading-relaxed">
                {t('shared.glossary.footer.text')}
              </p>
            </Reveal>
          </div>
          <div className="flex-shrink-0">
            <button
              onClick={handleArticleClick}
              className="bg-black text-white w-52 h-52 rounded-full flex flex-col items-center justify-center gap-3 hover:bg-clever-teal transition-all duration-500 group shadow-2xl"
            >
              <Hash size={24} className="group-hover:rotate-12 transition-transform" />
              <span className="text-[10px] font-bold tracking-[0.2em] uppercase">{t('shared.glossary.footer.cta')}</span>
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};
